import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { get, omit } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";

import Backdrop from "Components/Common/Backdrop";
import { getOptionValue } from "common/enums";
import ConsumerDetails from "Components/MeterInfoForm/ConsumerDetails";
import Modal from "Components/Common/Modal";
import Stepper from "common/Stepper";
import { meterKeys, meterReadingKeys } from "common/constant";
import MeterDetails, {
  inputFields as meterDetailsFields,
} from "Components/MeterInfoForm/MeterDetails";
import MeterReading, {
  inputFields as meterReadingFields,
} from "Components/MeterInfoForm/MeterReading";

import { UTILITY_CONSUMER } from "tagTypes";
import generateValidationSchema from "utils/generateValidationSchema";
import { filterEmptyStrings } from "utils";

import {
  useGetConsumerMeterDetailsByConsumerId,
  useGetMeterDetailsByMeterId,
  useLazyGetMeterDetailsByTypeId,
  usePostMeterInfoMutation,
} from "apis/UtilityConsumerMeter";

const UpdateConsumerFinalReading = ({ meterId, consumerId, handleClose }) => {
  const {
    data: { data: meterData } = {},
    isLoading: isUtilityConsumerMeterLoading,
  } = useGetMeterDetailsByMeterId(meterId, {
    isForConsumerDisconnection: true,
  });

  const {
    data: { data: utilityConsumer } = {},
    isLoading: isConsumerDataLoading,
    isFetching: isConsumerDataFetching,
  } = useGetConsumerMeterDetailsByConsumerId(consumerId);

  const [getMeterDetails, { isLoading: isMeterDetailsLoading }] =
    useLazyGetMeterDetailsByTypeId();

  const [updateMeterInfo, { isLoading }] = usePostMeterInfoMutation();

  const { meter, finalMeterReading } = meterData || {};

  const [meterType, setMeterType] = useState(
    getOptionValue("meterTypes", "Liberty 200")
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [currentStepName, setCurrentStepName] = useState("Consumer Details");

  const initialValues = useMemo(
    () => ({
      consumerInfo: {
        id: utilityConsumer?.id ?? "",
        name: utilityConsumer?.consumerName ?? "",
        address: utilityConsumer?.utilityAddress ?? "",
        serviceNo: utilityConsumer?.consumerNumber ?? "",
        typeOfConnection: utilityConsumer?.typeOfConnection ?? "",
        contractLoad: utilityConsumer?.contractLoad ?? "",
        typeOfTariff: utilityConsumer?.tariffName ?? "",
        meter: {
          id: utilityConsumer?.meter?.id ?? "",
          srNo: utilityConsumer?.meter?.srNo ?? "",
        },
      },
      meter: {
        id: meter?.id ?? 0,
        make: meter?.make ?? "",
        type: meter?.type ?? "",
        srNo: meter?.srNo ?? "",
        labNo: meter?.labNo ?? "",
        phaseOrWire: meter?.phaseOrWire ?? "",
        voltageRating: meter?.voltageRating ?? "",
        currentRating: meter?.currentRating ?? "",
        meterCTR: meter?.meterCTR ?? "",
        meterPTR: meter?.meterPTR ?? "",
        pulseOrkwH: meter?.pulseOrkwH ?? "",
        class: meter?.class ?? "",
        dialMF: meter?.dialMF ?? "",
      },
      finalMeterReading: {
        id: finalMeterReading?.id ?? 0,
        instantaneousVoltageV: finalMeterReading?.instantaneousVoltageV ?? "",
        instantaneousCurrentA: finalMeterReading?.instantaneousCurrentA ?? "",
        instantaneousActivePowerkW:
          finalMeterReading?.instantaneousActivePowerkW ?? "",
        activeImportEnergy: finalMeterReading?.activeImportEnergy ?? "",
        activeExportEnergy: finalMeterReading?.activeExportEnergy ?? "",
        currentImportMD: finalMeterReading?.currentImportMD ?? "",
        currentExportMD: finalMeterReading?.currentExportMD ?? "",
        rPhaseVoltage: finalMeterReading?.rPhaseVoltage ?? "",
        yPhaseVoltage: finalMeterReading?.yPhaseVoltage ?? "",
        bPhaseVoltage: finalMeterReading?.bPhaseVoltage ?? "",
        rPhaseCurrent: finalMeterReading?.rPhaseCurrent ?? "",
        yPhaseCurrent: finalMeterReading?.yPhaseCurrent ?? "",
        bPhaseCurrent: finalMeterReading?.bPhaseCurrent ?? "",
        neutralCurrent: finalMeterReading?.neutralCurrent ?? "",
        instActivePower: finalMeterReading?.instActivePower ?? "",
        instApparentPower: finalMeterReading?.instApparentPower ?? "",
        instantaneousPF: finalMeterReading?.instantaneousPF ?? "",
        frequency: finalMeterReading?.frequency ?? "",
        apparentImportEnergy: finalMeterReading?.apparentImportEnergy ?? "",
        apparentExportEnergy: finalMeterReading?.apparentExportEnergy ?? "",
        reactiveEnergyQ1: finalMeterReading?.reactiveEnergyQ1 ?? "",
        reactiveEnergyQ2: finalMeterReading?.reactiveEnergyQ2 ?? "",
        reactiveEnergyQ3: finalMeterReading?.reactiveEnergyQ3 ?? "",
        reactiveEnergyQ4: finalMeterReading?.reactiveEnergyQ4 ?? "",
        currentApparentImportMD:
          finalMeterReading?.currentApparentImportMD ?? "",
        currentApparentExportMD:
          finalMeterReading?.currentApparentExportMD ?? "",
        averagePowerFactor: finalMeterReading?.averagePowerFactor ?? "",
        phaseSequence: finalMeterReading?.phaseSequence ?? "",
        currentActiveImportPeakHourEnergyMWhTOD1:
          finalMeterReading?.currentActiveImportPeakHourEnergyMWhTOD1 ?? "",
        currentActiveImportPeakHourEnergyMWhTOD5:
          finalMeterReading?.currentActiveImportPeakHourEnergyMWhTOD5 ?? "",
        currentActiveExportPeakHourEnergyMWh:
          finalMeterReading?.currentActiveExportPeakHourEnergyMWh ?? "",
        currentActiveImportNightHourEnergyMWh:
          finalMeterReading?.currentActiveImportNightHourEnergyMWh ?? "",
        currentActiveExportNightHourEnergyMWh:
          finalMeterReading?.currentActiveExportNightHourEnergyMWh ?? "",
        currentActiveImportRestHourEnergyMWh:
          finalMeterReading?.currentActiveImportRestHourEnergyMWh ?? "",
        currentActiveExportRestHourEnergyMWh:
          finalMeterReading?.currentActiveExportRestHourEnergyMWh ?? "",
        hist1ActiveImportEnergy:
          finalMeterReading?.hist1ActiveImportEnergy ?? "",
        hist1ActiveExportEnergy:
          finalMeterReading?.hist1ActiveExportEnergy ?? "",
        hist2ActiveImportEnergy:
          finalMeterReading?.hist2ActiveImportEnergy ?? "",
        hist2ActiveExportEnergy:
          finalMeterReading?.hist2ActiveExportEnergy ?? "",
        hist1ActiveImportMD: finalMeterReading?.hist1ActiveImportMD ?? "",
        hist1ActiveExportMD: finalMeterReading?.hist1ActiveExportMD ?? "",
        hist2ActiveImportMD: finalMeterReading?.hist2ActiveImportMD ?? "",
        hist2ActiveExportMD: finalMeterReading?.hist2ActiveExportMD ?? "",
        hist1ApparentImportMD: finalMeterReading?.hist1ApparentImportMD ?? "",
        hist1ApparentExportMD: finalMeterReading?.hist1ApparentExportMD ?? "",
        hist2ApparentImportMD: finalMeterReading?.hist2ApparentImportMD ?? "",
        hist2ApparentExportMD: finalMeterReading?.hist2ApparentExportMD ?? "",
        currentDefrauded: finalMeterReading?.currentDefrauded ?? "",
        cumulativeTamperCount: finalMeterReading?.cumulativeTamperCount ?? "",
        activeExportSetOffEnergyTOD1:
          finalMeterReading?.activeExportSetOffEnergyTOD1 ?? "",
        activeImportSetOffEnergyTOD1:
          finalMeterReading?.activeImportSetOffEnergyTOD1 ?? "",
        activeExportSetOffEnergyTOD3:
          finalMeterReading?.activeExportSetOffEnergyTOD3 ?? "",
        activeImportSetOffEnergyTOD3:
          finalMeterReading?.activeImportSetOffEnergyTOD3 ?? "",
      },
      extraValues: {
        isMeterChanged: false,
        meterId: meter?.id,
        srNo: meter?.srNo ?? "",
      },
    }),
    [utilityConsumer, meter, finalMeterReading]
  );

  const validationSchemas = useMemo(
    () => ({
      "Consumer Details": Yup.object(),
      "Meter Details": Yup.object({
        meter: Yup.object(generateValidationSchema(meterDetailsFields())),
      }),
      "Final Meter Reading": Yup.object({
        finalMeterReading: Yup.object(
          generateValidationSchema(meterReadingFields[meterType])
        ),
      }),
    }),
    [meterType]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchemas[currentStepName],
    initialValues,
    onSubmit: async (values, { setTouched }) => {
      const isLastStep = currentStep === steps.length - 1;
      if (isLastStep) {
        await updateMeterInfo({
          payload: filterEmptyStrings(
            omit(values, ["extraValues", "consumerInfo"])
          ),
          query: {
            utilityConsumerId: consumerId,
            isForConsumerDisconnection: true,
          },
          toastMessage: "Meter details updated successfully",
          tags: [UTILITY_CONSUMER],
        });
        handleClose();
      } else {
        setCurrentStep((prev) => prev + 1);
        setTouched({});
      }
    },
  });

  const {
    values: {
      meter: { type },
    },
    setFieldValue,
    handleSubmit,
  } = formik;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Consumer Details",
        isCompleted: currentStep >= 1,
        component: <ConsumerDetails formik={formik} />,
      },
      {
        id: 2,
        name: "Meter Details",
        isCompleted: currentStep >= 2,
        component: <MeterDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Final Meter Reading",
        isCompleted: currentStep >= 3,
        component: (
          <MeterReading
            parentKey="finalMeterReading"
            formik={formik}
            finalMeterReading={finalMeterReading || {}}
            isOnlyFinalReadingRequired
          />
        ),
      },
    ],
    [formik]
  );

  useEffect(() => {
    if (type !== meterData?.meter?.type) {
      (async () => {
        try {
          const { data } = await getMeterDetails({
            meterVariantType: type,
          });

          meterKeys.forEach((key) =>
            setFieldValue(`meter.${key}`, get(data, key) ?? "")
          );
          setFieldValue("extraValues.meterId", data.meter.id);
        } catch (error) {}
      })();
      meterReadingKeys.forEach(({ key }) => {
        setFieldValue(`finalMeterReading.${key}`, "");
      });
    }
    setMeterType(type);
  }, [type]);

  useEffect(() => {
    const stepName = steps.find((_, index) => index === currentStep).name;
    if (stepName !== currentStepName) {
      setCurrentStepName(stepName);
    }
  }, [currentStep, steps]);

  if (
    isUtilityConsumerMeterLoading ||
    isConsumerDataLoading ||
    isConsumerDataFetching
  ) {
    return <Backdrop open />;
  }

  return (
    <>
      <Backdrop open={isMeterDetailsLoading} />
      <Modal
        isOpen
        handleClose={handleClose}
        title="Meter Information"
        maxWidth="lg"
      >
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          isLoading={isLoading}
          formik={formik}
          style={{ minHeight: "300px" }}
          isDraftDisabled
        />
      </Modal>
    </>
  );
};

UpdateConsumerFinalReading.propTypes = {
  meterId: PropTypes.number,
  consumerId: PropTypes.number,
  handleClose: PropTypes.func,
};

export default UpdateConsumerFinalReading;
